import { Injectable } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { ConfirmDialogComponent, ConfirmDialogData } from "@cotecna/dialogs";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "src/environments/environment";

import { HttpService } from "../../session/services/http.service";
import { CloneInspectionData } from "../dialogs/clone-inspection/clone-inspection-dialog/clone-inspection-data";
import { LoadingComponent } from "../dialogs/loading/loading.component";
import { FilterForm } from "../inspections/filters/filter-form-interface";
import { InspectionInMainPage } from "../model/inspection";
import {
  InspectionMainSearch,
  ItemsMainSearch,
} from "../model/main-search-result";
import { SearchType } from "../model/searchType";

@Injectable({
  providedIn: "root",
})
export class InspectionListService {
  private readonly INSPECTION_NEW_STATE: string = "new";

  public constructor(
    private httpService: HttpService,
    private router: Router,
    private dialog: MatDialog,
  ) {}

  public getActiveNominatedInspections(
    currentPage: number,
    pageSize: number,
    order: string,
    descending: boolean,
  ): Promise<InspectionInMainPage[]> {
    return this.httpService
      .get(
        `${environment.webApiUrl}/api/inspection/GetActiveNominatedInspections?currentPage=${currentPage}&pageSize=${pageSize}&order=${order}&descending=${descending}`,
        { withCredentials: true },
      )
      .toPromise();
  }

  public getClosedNominatedInspections(
    currentPage: number,
    pageSize: number,
    order: string,
    descending: boolean,
  ): Promise<InspectionInMainPage[]> {
    return this.httpService
      .get(
        `${environment.webApiUrl}/api/inspection/GetClosedNominatedInspections?currentPage=${currentPage}&pageSize=${pageSize}&order=${order}&descending=${descending}`,
        { withCredentials: true },
      )
      .toPromise();
  }

  public getByAccount(
    currentPage: number,
    pageSize: number,
    order: string = "",
    desc: boolean = false,
  ): Observable<InspectionInMainPage[]> {
    return this.httpService
      .get(
        `${environment.webApiUrl}/api/inspection/GetPaginated?currentPage=${currentPage}&pageSize=${pageSize}&order=${order}&descending=${desc}`,
        { withCredentials: true },
      )
      .pipe(
        map((inspections: InspectionInMainPage[]) => {
          return inspections;
        }),
      );
  }

  public getByClient(
    currentPage: number,
    pageSize: number,
    order: string = "",
    desc: boolean = false,
  ): Observable<InspectionInMainPage[]> {
    return this.httpService
      .get(
        `${environment.webApiUrl}/api/inspection/GetClientInspections?currentPage=${currentPage}&pageSize=${pageSize}&order=${order}&descending=${desc}`,
        { withCredentials: true },
      )
      .pipe(
        map((inspections: InspectionInMainPage[]) => {
          return inspections;
        }),
      );
  }

  public getFiltered(
    currentPage: number,
    pageSize: number,
    order: string = "",
    desc: boolean = false,
    filterFormToSend: FilterForm,
    active: boolean,
  ): Promise<InspectionInMainPage[]> {
    return this.httpService
      .get(
        `${environment.webApiUrl}/api/inspection/Filtered?currentPage=${currentPage}&pageSize=${pageSize}&order=${order}&descending=${desc}&active=${active}&state=${filterFormToSend.status}&origin=${filterFormToSend.origin}&externalId=${filterFormToSend.inspectionId}&client=${filterFormToSend.client}&inspectionReference=${filterFormToSend.inspectionReference}&type=${filterFormToSend.type}&dateTo=${filterFormToSend.dateTo}&dateFrom=${filterFormToSend.dateFrom}&creationDateFrom=${filterFormToSend.creationDateFrom}&creationDateTo=${filterFormToSend.creationDateTo}&closeDate=${filterFormToSend.closeDate}&vesselName=${filterFormToSend.vesselName}&timeManagementState=${filterFormToSend.timeManagementState}`,
        { withCredentials: true },
      )
      .toPromise();
  }

  public getClosedInspectionsByClient(
    currentPage: number,
    pageSize: number,
    order: string = "",
    desc: boolean = false,
  ): Observable<InspectionInMainPage[]> {
    return this.httpService
      .get(
        `${environment.webApiUrl}/api/inspection/GetClosedClientInspections?currentPage=${currentPage}&pageSize=${pageSize}&order=${order}&descending=${desc}`,
        { withCredentials: true },
      )
      .pipe(
        map((inspections: InspectionInMainPage[]) => {
          return inspections;
        }),
      );
  }

  public getClosedInspections(
    currentPage: number,
    pageSize: number,
    order: string = "",
    desc: boolean = false,
  ): Observable<InspectionInMainPage[]> {
    return this.httpService
      .get(
        `${environment.webApiUrl}/api/inspection/GetClosedInspections?currentPage=${currentPage}&pageSize=${pageSize}&order=${order}&descending=${desc}`,
        { withCredentials: true },
      )
      .pipe(
        map((inspections: InspectionInMainPage[]) => {
          return inspections;
        }),
      );
  }

  public getServiceProviderCoordinatorActiveInspections(
    currentPage: number,
    pageSize: number,
    order: string = "",
    desc: boolean = false,
  ): Observable<InspectionInMainPage[]> {
    return this.httpService
      .get(
        `${environment.webApiUrl}/api/inspection/GetExternalCoordinatorActiveInspections?currentPage=${currentPage}&pageSize=${pageSize}&order=${order}&descending=${desc}`,
        { withCredentials: true },
      )
      .pipe(
        map((inspections: InspectionInMainPage[]) => {
          return inspections;
        }),
      );
  }

  public getServiceProviderCoordinatorClosedInspections(
    currentPage: number,
    pageSize: number,
    order: string = "",
    desc: boolean = false,
  ): Observable<InspectionInMainPage[]> {
    return this.httpService
      .get(
        `${environment.webApiUrl}/api/inspection/GetExternalCoordinatorClosedInspections?currentPage=${currentPage}&pageSize=${pageSize}&order=${order}&descending=${desc}`,
        { withCredentials: true },
      )
      .pipe(
        map((inspections: InspectionInMainPage[]) => {
          return inspections;
        }),
      );
  }

  public cloneInspection(
    cloneInspectionData: CloneInspectionData,
  ): Promise<void> {
    return this.httpService
      .post(
        `${environment.webApiUrl}/api/inspection/cloneInspection`,
        cloneInspectionData,
        { withCredentials: true },
      )
      .toPromise();
  }

  public deleteInspection(inspectionId: any): Promise<void> {
    return this.httpService
      .delete(`${environment.webApiUrl}/api/inspection`, {
        body: [inspectionId],
        withCredentials: true,
      })
      .toPromise();
  }

  public async search(
    searchTerm: string,
    searchType: number,
    totalTimes?: number,
  ): Promise<void> {
    let loadingDialog: MatDialogRef<any> = null;
    try {
      loadingDialog = this.openLoadingDialog("Searching");
      if (searchType === SearchType.inspections) {
        const inspectionSearchResult: InspectionMainSearch =
          await this.searchInspection(searchTerm);
        if (inspectionSearchResult) {
          if (
            inspectionSearchResult.inspectionState.toLowerCase() ===
            this.INSPECTION_NEW_STATE
          ) {
            await this.router.navigate([
              `new-inspection/${inspectionSearchResult.inspectionId}`,
            ]);
          } else {
            await this.router.navigate([""]);
            await this.router.navigate([
              `details/${inspectionSearchResult.inspectionId}/info`,
            ]);
          }
        } else {
          this.openNoResultsDialog();
        }
      } else {
        if (totalTimes) {
          await this.router.navigate([`item-search-result`], {
            queryParams: { searchTerm: searchTerm, repeatedSearch: totalTimes },
          });
        } else {
          await this.router.navigate([`item-search-result`], {
            queryParams: { searchTerm: searchTerm },
          });
        }
      }
    } catch {
      if (searchType === SearchType.inspections) {
        await this.router.navigate([""]);
      }
      this.openNoResultsDialog();
    } finally {
      loadingDialog?.close();
    }
  }

  private openLoadingDialog(message: string): MatDialogRef<any> {
    return this.dialog.open(LoadingComponent, {
      data: <LoadingComponent>{
        loadingText: message,
      },
      panelClass: "dialog",
    });
  }

  private openNoResultsDialog(): MatDialogRef<any> {
    return this.dialog.open(ConfirmDialogComponent, {
      data: <ConfirmDialogData>{
        icon: "warning",
        okButton: { text: "BACK", color: "primary" },
        title: "No results found",
        message: "There are no matches for the inserted data.",
      },
      autoFocus: false,
    });
  }

  public async searchInspection(searchTerm: string): Promise<any> {
    return this.httpService
      .get(
        `${environment.webApiUrl}/api/search/inspection?inspectionName=${searchTerm}`,
        { withCredentials: true },
      )
      .toPromise();
  }

  public searchItems(searchTerm: string): Promise<ItemsMainSearch[]> {
    return this.httpService
      .get(
        `${environment.webApiUrl}/api/search/inspections?itemName=${searchTerm}`,
        { withCredentials: true },
      )
      .toPromise();
  }
}
